import React, { useCallback, useEffect, useState } from "react";

import Button from "src/components/ui/Button";
import Dropdown from "src/components/ui/Dropdown";

import productApis from "src/api/product";
import tenantUserApis from "src/api/tenantUser";
import { Product } from "src/types/product.types";

interface InventoryTransactionsFiltersProps {
  selectedFilters: any;
  handleReset: () => void;
  onFilterApply: (filter: any) => void;
}

const InventoryTransactionsFilters: React.FC<
  InventoryTransactionsFiltersProps
> = ({ selectedFilters = {}, handleReset, onFilterApply }) => {
  const [filters, setFilters] = useState({
    referenceType: "",
    productId: "",
    createdBy: "",
    ...selectedFilters,
  });

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const filterTypes = [
    { label: "Adjustment", value: "adjustment" },
    { label: "Purchase", value: "purchase" },
    { label: "Sale", value: "sale" },
  ];

  // Load products & users once
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [productRes, userRes] = await Promise.all([
        productApis.getProductList({ limit: 1000 }),
        tenantUserApis.getTenantUserList({
          limit: 1000,
        }),
      ]);

      console.log("Filter data loaded:", { productRes, userRes });

      if (productRes?.data) setAllProducts(productRes?.data || []);
      if (userRes?.data) setUsers(userRes?.data || []);
    } catch (error) {
      console.error("Error loading filter data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Trigger once when popover opens
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = () => {
    const appliedFilters = { ...filters };

    const selectedProduct = allProducts.find(
      (p) => p._id === filters.productId
    );
    if (selectedProduct) {
      appliedFilters.productName = selectedProduct.name;
    } else {
      delete appliedFilters.productName;
    }

    const selectedUser = users.find((u) => u._id === filters.createdBy);
    if (selectedUser) {
      appliedFilters.createdByName = `${selectedUser.firstName} (${selectedUser.role.name})`;
    } else {
      delete appliedFilters.createdByName;
    }
    onFilterApply(appliedFilters);
  };

  return (
    <div className="space-y-4">
      {loading && (
        <p className="text-xs text-gray-400 mb-2">Loading filters...</p>
      )}

      <Dropdown
        label="Transaction Type"
        options={filterTypes}
        value={filters.referenceType}
        onChange={(value) =>
          setFilters((prev: any) => ({ ...prev, referenceType: value }))
        }
        fullWidth
      />

      <Dropdown
        label="Product"
        options={((allProducts || []) as any).map((p: any) => ({
          label: `${p.name} (${p.productId})`,
          value: p._id,
        }))}
        value={filters.productId}
        onChange={(value) =>
          setFilters((prev: any) => ({ ...prev, productId: value }))
        }
        fullWidth
      />

      <Dropdown
        label="Created By"
        options={users.map((u) => ({
          label: `${u.firstName} (${u.role.name})`,
          value: u._id,
        }))}
        value={filters.createdBy}
        onChange={(value) =>
          setFilters((prev: any) => ({ ...prev, createdBy: value }))
        }
        fullWidth
      />

      <div className="mt-4 flex items-center justify-between gap-4">
        <Button variant="outline" onClick={handleReset} fullWidth>
          Reset
        </Button>
        <Button variant="primary" onClick={handleSubmit} fullWidth>
          Apply
        </Button>
      </div>
    </div>
  );
};

export default InventoryTransactionsFilters;
